import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, filter, map, of, switchMap } from 'rxjs';

import { injectTellentAssistantResourceIdGetter } from '@core/tellent-assistant-contracts';
import { toChatMessages } from '@core/tellent-assistant-util';

import { TellentAssistantMastraClientService } from '../tellent-assistant-mastra.client.service';
import {
  TellentAssistantCurrentThreadActions,
  TellentAssistantCurrentThreadSelectors,
} from './tellent-assistant-current-thread.state';
import { TellentAssistantThreadsActions } from './tellent-assistant-threads.state';

export const clearCurrentThreadOnDelete = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(TellentAssistantThreadsActions.removeOnePessimisticSuccess),
      concatLatestFrom(() => store.select(TellentAssistantCurrentThreadSelectors.data)),
      filter(
        ([action, current]) => current !== null && action.payload.data.id === current.threadId,
      ),
      map(() => TellentAssistantCurrentThreadActions.clear()),
    ),
  { functional: true },
);

export const loadTellentAssistantCurrentThread = createEffect(
  (
    actions$ = inject(Actions),
    client = inject(TellentAssistantMastraClientService),
    getResourceId = injectTellentAssistantResourceIdGetter({ optional: true }) ?? (() => null),
  ) =>
    actions$.pipe(
      ofType(TellentAssistantCurrentThreadActions.fetch),
      switchMap(({ payload: { threadId } }) => {
        const resourceId = getResourceId();
        if (!resourceId) return EMPTY;
        return client.loadMessages(threadId, resourceId).pipe(
          map(messages =>
            TellentAssistantCurrentThreadActions.fetchSuccess({
              payload: { threadId, messages: toChatMessages(messages) },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              TellentAssistantCurrentThreadActions.fetchFailed({
                payload: error,
              }),
            ),
          ),
        );
      }),
    ),
  { functional: true },
);
